import React, { useEffect, useState, Dispatch, SetStateAction, useMemo } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import { useModal } from '../../function/useModal';
import ConfirmDialog, { ConfirmDialogResponse } from '../atoms/Dialog/ConfirmDialog';

export type MenuContents = {
  name: string;
  component: React.ComponentType<any>;
  tag: string;
};

export class SettingsState {
  private _update: boolean;
  private _target: object;
  private _callback: (param: object) => Promise<string>;
  private _setIdx?: Dispatch<SetStateAction<number>>;
  private _confirm?: (state: SettingsState, tag: string) => void;
  private _param: object | undefined;
  private _saveClose: boolean;
  private _cancelFlip: boolean;
  private _parentDialogAction?: () => void;
  selectIndexNo?: number;

  /**
   * コンストラクタ
   */
  constructor() {
    console.log('settingsState initialize...');
    this._update = false;
    this._target = {};
    this._callback = this.dummyCallback;
    this._param = {};
    this._saveClose = false;
    this._cancelFlip = false;
  }

  set DispatchIndex(dispatch: Dispatch<SetStateAction<number>>) {
    this._setIdx = dispatch;
  }
  set SaveConfirmFunction(func: (state: SettingsState, tag: string) => void) {
    this._confirm = func;
  }
  set ParentDialogAction(func: () => void) {
    this._parentDialogAction = func;
  }

  /**
   * 更新フラグ
   */
  get IsUpdate(): boolean {
    return this._update;
  }
  set IsUpdate(value: boolean) {
    this._update = value;
  }

  /**
   * 関連情報
   */
  get Target(): object {
    return this._target;
  }
  set Target(data: object) {
    this._target = data;
  }

  /**
   * ページ呼び出しパラメータ
   */
  get PageParam(): object | undefined {
    return this._param;
  }
  set PageParam(param: object | undefined) {
    this._param = param;
  }

  /**
   * 保存処理
   */
  get SaveFunction(): (param: object) => Promise<string> {
    return this._callback;
  }
  set SaveFunction(callback: (param: object) => Promise<string>) {
    this._callback = callback;
  }

  /**
   * 保存処理実行フラグ
   */
  get IsSaveClose(): boolean {
    return this._saveClose;
  }
  set IsSaveClose(isSave: boolean) {
    this._saveClose = isSave;
  }

  /**
   * キャンセル状態
   */
  get CancelCondition(): boolean {
    return this._cancelFlip;
  }

  dummyCallback = (param: object): Promise<string> => {
    return new Promise((resolve, reject) => {
      reject('');
    });
  };

  /**
   * 保存処理実行
   * @returns
   */
  SaveSetting(): Promise<string> {
    return this._callback(this._target);
  }

  /**
   * 設定画面を閉じる
   */
  CloseSetting() {
    if (this._setIdx) {
      this._setIdx(-1);
    }
  }

  /**
   * 更新状態のキャンセル
   */
  UpdateCancel() {
    this._cancelFlip = !this._cancelFlip;
  }

  /**
   * 指定のメニューを直接開く
   * @param tagidx メニュー番号
   * @param param メニューパラメータ
   */
  JumpMenu(tag: string, param?: object) {
    this._param = param;
    if (this._confirm) {
      this._confirm(this, tag);
    }
  }

  /**
   * 親のダイアログを閉じる処理
   */
  CloseDialog() {
    if (this._parentDialogAction) {
      this._parentDialogAction();
    }
  }
}

const useStyles = makeStyles(
  createStyles({
    menuPanel: {
      display: 'flex',
      overflow: 'auto',
      width: '100%',
      marginTop: '4px',
      marginBottom: '4px',
      //marginRight: '10px',
    },
    menuButton: {
      width: '100%',
      borderStyle: 'solid',
      borderColor: '#c0c0c0',
      borderWidth: 'thin',
      marginLeft: '2px',
      mariginRight: '2px',
      marginBottom: '2px',
      textTransform: 'none',
    },
  })
);

type SelectSettingsProps = {
  menuList: MenuContents[];
  isSelectMenu?: number;
  dialogColoseAction?: () => void;
  selectIndexNo?: number;
};

const SelectSettings: React.FC<SelectSettingsProps> = (props: SelectSettingsProps) => {
  const classes = useStyles();
  // const [selIdx, setSelIdx] = useState<number>(props.setIdx);
  // const [noCheckCount, setNoCheckCount] = useState<number>(0);
  // const [editData, ] = useState<SettingsState>(new SettingsState());
  const [target, setTarget] = useState<string>('');
  const [flgDlg, actDlg] = useModal<ConfirmDialogResponse>();

  const editData = useMemo(() => new SettingsState(), []);
  useEffect(() => {
    //画面表示時にメニュー呼び出しを行う機能
    if (props.isSelectMenu !== undefined) {
      changeMenu(editData, props.menuList[0].tag)
    }
    editData.SaveConfirmFunction = changeMenu;
    if (props.dialogColoseAction !== undefined) {
      editData.CloseDialog = props.dialogColoseAction;
    }
    if (props.selectIndexNo !== undefined) {
      editData.selectIndexNo = props.selectIndexNo;
    }
  }, []);

  const changeMenu = (editState: SettingsState, targetTag: string) => {
    console.debug('Change menu:', targetTag);
    if (editState.IsUpdate) {
      actDlg
        .open()
        .apply((_) => {
          editState
            .SaveSetting()
            .then((_) => {
              editState.IsUpdate = false;

              setTarget(targetTag);
            })
            .catch((reason) => {
              console.error(reason);
            });
        })
        .cancel((_) => {
          editState.IsUpdate = false;

          setTarget(targetTag);
        });
    } else {
      editState.IsUpdate = false;

      setTarget(targetTag);
    }
  };

  const targetPage = props.menuList.find((pg) => pg.tag == target);
  const settingComponent = targetPage ? React.createElement(targetPage.component, { editData: editData }) : null;
  const confirm = flgDlg ? <ConfirmDialog dialog={actDlg} /> : null;

  return (
    <>
      <div className={classes.menuPanel}>
        {props.menuList.map((mn, idx) => {
          return (
            <Button
              key={idx}
              className={classes.menuButton}
              onClick={(e) => {
                changeMenu(editData, mn.tag);
              }}
              variant="contained"
              color={mn.tag == target ? 'primary' : 'inherit'}
            >
              {mn.name}
            </Button>
          );
        })}
      </div>
      {settingComponent}
      {confirm}
    </>
  );
};

export default SelectSettings;
